/**
 * Arame: automatic gift with purchase
 *
 * - Adds the gift to the cart when the cart reaches the threshold
 * - Removes it when the cart drops below the threshold, or the promo ends
 * - Keeps the gift at quantity 1
 * - Respects a shopper who removes the gift (offers an "Add to cart" button instead)
 * - Shows a progress message in the cart drawer and cart page
 *
 * The $0 price comes from a Shopify automatic discount, not from this script.
 * Settings live in snippets/gift-with-purchase.liquid.
 *
 * Built for the Pipeline theme, which fires `theme:cart:change` whenever the
 * cart changes and redraws the drawer on `theme:cart:reload`.
 */
(() => {
  const configEl = document.getElementById('gwp-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    console.warn('[GWP] Could not read config', err);
    return;
  }

  const variantId = Number(config.variantId);
  if (!variantId) return; // No gift product found; nothing to add or clean up.

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const STATE_KEY = 'arame_gwp_' + variantId;

  // ── Is the promo running right now? ──────────────────────────────────────
  // When it isn't, the script stays on in cleanup mode and removes leftover
  // gifts, so nobody is charged full price after the discount ends.
  const now = Date.now();
  const startsAt = config.startsAt ? Date.parse(config.startsAt) : NaN;
  const endsAt = config.endsAt ? Date.parse(config.endsAt) : NaN;
  const isLive =
    config.enabled === true &&
    config.available === true &&
    (isNaN(startsAt) || now >= startsAt) &&
    (isNaN(endsAt) || now < endsAt);

  // Convert the threshold into the shopper's currency (Shopify Markets).
  const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  const thresholdCents = Math.round(Number(config.threshold) * 100 * rate);

  // ── Session state ────────────────────────────────────────────────────────
  function loadState() {
    try {
      return Object.assign({ hadGift: false, declined: false, unavailable: false },
        JSON.parse(sessionStorage.getItem(STATE_KEY)) || {});
    } catch (err) {
      return { hadGift: false, declined: false, unavailable: false };
    }
  }

  function saveState(state) {
    try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (err) { /* private mode */ }
  }

  // ── Cart helpers ─────────────────────────────────────────────────────────
  const isGiftLine = (item) => item.variant_id === variantId;

  function qualifyingSubtotal(cart) {
    const others = cart.items.filter((item) => !isGiftLine(item));
    if (config.basis === 'after_discounts') {
      const lines = others.reduce((sum, item) => sum + item.final_line_price, 0);
      const orderLevel = (cart.cart_level_discount_applications || [])
        .reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
      return Math.max(0, lines - orderLevel);
    }
    return others.reduce((sum, item) => sum + item.original_line_price, 0);
  }

  async function request(path, body) {
    const res = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const err = new Error(path + ' failed with ' + res.status);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  const getCart = () => request('cart.js');

  const addGift = () => request('cart/add.js', {
    items: [{ id: variantId, quantity: 1, properties: { _gwp: 'true' } }],
  });

  const updateLines = (updates) => request('cart/update.js', { updates });

  // Tell Pipeline the cart changed so the header count, drawer and totals redraw.
  // On the full cart page, Pipeline answers `theme:cart:reload` with a page reload.
  function refreshTheme(cart) {
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
  }

  // ── Core logic ───────────────────────────────────────────────────────────
  async function reconcile(cart) {
    const state = loadState();
    const giftLines = cart.items.filter(isGiftLine);
    const hasGift = giftLines.length > 0;
    const hasOtherItems = cart.items.some((item) => !isGiftLine(item));
    const qualifies = isLive && hasOtherItems && qualifyingSubtotal(cart) >= thresholdCents;
    let changed = false;
    let hasGiftNow = hasGift;

    // Shopper removed the gift themselves while still qualifying.
    if (qualifies && state.hadGift && !hasGift) state.declined = true;
    // Shopper added it back (or it's already there).
    if (qualifies && hasGift) state.declined = false;
    // Dropping below the threshold resets the choice for next time.
    if (!qualifies) state.declined = false;

    if (qualifies && hasGift) {
      // Keep exactly one gift.
      const [keep, ...extras] = giftLines;
      const updates = {};
      extras.forEach((line) => { updates[line.key] = 0; });
      if (keep.quantity !== 1) updates[keep.key] = 1;
      if (Object.keys(updates).length) {
        await updateLines(updates);
        changed = true;
      }
    } else if (qualifies && !hasGift && !state.declined && !state.unavailable) {
      try {
        await addGift();
        changed = true;
        hasGiftNow = true;
      } catch (err) {
        // 422 usually means sold out. Stop trying for this session.
        state.unavailable = true;
        console.warn('[GWP] Gift could not be added', err);
      }
    } else if (!qualifies && hasGift) {
      const updates = {};
      giftLines.forEach((line) => { updates[line.key] = 0; });
      await updateLines(updates);
      changed = true;
      hasGiftNow = false;
    }

    state.hadGift = hasGiftNow;
    saveState(state);
    return changed;
  }

  let busy = false;
  let pending = false;

  async function sync(knownCart) {
    if (busy) { pending = true; return; }
    busy = true;
    try {
      let cart = knownCart || await getCart();
      if (await reconcile(cart)) {
        cart = await getCart();
        renderMessages(cart);
        refreshTheme(cart);
      } else {
        renderMessages(cart);
      }
    } catch (err) {
      console.warn('[GWP]', err);
    } finally {
      busy = false;
      if (pending) {
        pending = false;
        sync(); // Re-check with a fresh cart.
      }
    }
  }

  // ── Progress message ─────────────────────────────────────────────────────
  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  };

  const fill = (text, amount) => String(text || '')
    .replace(/\{amount\}/g, amount)
    .replace(/\{gift\}/g, config.giftName || 'gift');

  function renderMessages(cart) {
    const slots = document.querySelectorAll('[data-gwp-message]');
    if (!slots.length) return;

    const state = loadState();
    const subtotal = qualifyingSubtotal(cart);
    const hasGift = cart.items.some(isGiftLine);
    const show = isLive && !state.unavailable;
    const remaining = Math.max(0, thresholdCents - subtotal);
    const progress = thresholdCents > 0 ? Math.min(1, subtotal / thresholdCents) : 1;

    let text = '';
    let button = false;
    if (remaining > 0) {
      text = subtotal > 0
        ? fill(config.messages.progress, money(remaining))
        : fill(config.messages.teaser, money(thresholdCents));
    } else if (hasGift) {
      text = fill(config.messages.unlocked, '');
    } else {
      text = fill(config.messages.declined, '');
      button = true;
    }

    // Same wheel as Pipeline's free shipping message (18px, r=7). The theme's
    // math: empty = C, full = C / 2, where C = 28π.
    const C = 87.96459430051421;
    const offset = C - progress * (C / 2);
    const svgNS = 'http://www.w3.org/2000/svg';

    slots.forEach((slot) => {
      slot.hidden = !show;
      if (!show) return;
      slot.innerHTML = '';

      // Uses the theme's classes only (no data-cart-* attributes), so the
      // theme's shipping script leaves this wheel alone.
      const row = document.createElement('p');
      row.className = 'cart__message gwp-message__row';

      const graph = document.createElement('small');
      graph.className = 'cart__graph';
      graph.setAttribute('role', 'progressbar');
      graph.setAttribute('aria-valuemin', '0');
      graph.setAttribute('aria-valuemax', '100');
      graph.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      graph.setAttribute('aria-label', 'Progress toward your free gift');

      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('height', '18');
      svg.setAttribute('width', '18');
      svg.setAttribute('aria-hidden', 'true');
      const track = document.createElementNS(svgNS, 'circle');
      const fillCircle = document.createElementNS(svgNS, 'circle');
      [track, fillCircle].forEach((c) => {
        c.setAttribute('r', '7');
        c.setAttribute('cx', '9');
        c.setAttribute('cy', '9');
      });
      fillCircle.setAttribute('class', 'cart__graph-progress');
      fillCircle.setAttribute('stroke-dasharray', C + ' ' + C);
      fillCircle.style.strokeDashoffset = String(offset);
      svg.appendChild(track);
      svg.appendChild(fillCircle);
      graph.appendChild(svg);
      row.appendChild(graph);

      const label = document.createElement('span');
      label.className = 'gwp-message__text';
      label.textContent = text;
      row.appendChild(label);
      slot.appendChild(row);

      if (button) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'gwp-message__button';
        btn.textContent = config.messages.addButton || 'Add to cart';
        btn.addEventListener('click', () => {
          const s = loadState();
          s.declined = false;
          s.hadGift = false;
          saveState(s);
          btn.disabled = true;
          sync();
        });
        label.appendChild(document.createTextNode(' '));
        label.appendChild(btn);
      }
    });
  }

  // ── Wire up ──────────────────────────────────────────────────────────────
  document.addEventListener('theme:cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    sync(cart && Array.isArray(cart.items) ? cart : undefined);
  });

  // Other scripts (like the bamboo add-on) may fire this.
  document.addEventListener('cart:updated', () => sync());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
})();
